import { cn } from "@babackapp/customer-ui/lib/utils";
import { motion, Variants } from "motion/react";
import React from "react";

export function AnimatedList({
  className,
  children,
  ...props
}: React.ComponentProps<typeof motion.div>) {
  const childCount = React.Children.count(children);
  const shouldStagger = childCount <= 5;

  const animatedListVariants: Variants = {
    enter: {
      transition: {
        staggerChildren: shouldStagger ? 0.08 : 0,
      },
    },
    visible: {
      transition: {
        ease: [0.16, 1, 0.3, 1], // expo out
        staggerChildren: shouldStagger ? 0.08 : 0,
      },
    },
    exit: {
      opacity: 0,
      x: -20,
      transition: {
        staggerChildren: shouldStagger ? 0.05 : 0,
        ease: [0.7, 0, 0.84, 0], // expo in
        duration: 0.2,
      },
    },
  };
  return (
    <motion.div
      variants={animatedListVariants}
      initial="enter"
      animate="visible"
      exit="exit"
      className={cn("w-full flex flex-col gap-2", className)}
      {...props}
    >
      {children}
    </motion.div>
  );
}

type AnimatedListItemProps = React.ComponentProps<typeof motion.div> & {
  disableVariants?: Partial<Record<"enter" | "exit" | "visible", boolean>>;
  disabled?: boolean;
};

export function AnimatedListItem({
  className,
  disableVariants,
  disabled = false,
  variants,
  ...props
}: AnimatedListItemProps) {
  const targetOpacity = disabled ? 0.5 : 1;

  const animatedListItemVariants: Variants = variants ?? {
    enter: { opacity: 0, x: 20 },
    visible: {
      opacity: targetOpacity,
      x: 0,
      transition: {
        duration: 1.0,
        ease: [0.16, 1, 0.3, 1], // expo out
      },
    },
    exit: {
      opacity: 0,
      x: -20,
      transition: {
        duration: 0.2,
        ease: [0.7, 0, 0.84, 0], // expo in
      },
    },
  };

  return (
    <motion.div
      inert={disabled}
      aria-disabled={disabled}
      className={cn("w-full flex-1", className)}
      variants={{
        ...(!disableVariants?.enter && {
          enter: animatedListItemVariants.enter,
        }),
        ...(!disableVariants?.visible && {
          visible: animatedListItemVariants.visible,
        }),
        ...(!disableVariants?.exit && { exit: animatedListItemVariants.exit }),
      }}
      initial="enter"
      animate="visible"
      exit="exit"
      {...props}
    />
  );
}
